import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { adminApi, AdminUser } from '../utils/api';

interface AdminAuthContextType {
  admin: AdminUser | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

interface AdminAuthProviderProps {
  children: ReactNode;
}

export const AdminAuthProvider: React.FC<AdminAuthProviderProps> = ({ children }) => {
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Auth lives in an httpOnly cookie the JS can't read, so establish the
    // session by asking the server who we are. (#774)
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await adminApi.getCurrentAdmin();
      setAdmin(response.data);
    } catch (error) {
      // Not authenticated (or the session expired).
      setAdmin(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      // The server sets the httpOnly session + CSRF cookies; the response body
      // carries only the (non-sensitive) admin profile. (#774)
      const response = await adminApi.login({ email, password });
      setAdmin(response.data.admin);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } };
      throw new Error(err.response?.data?.detail || 'Login failed. Please try again.');
    }
  };

  const logout = async () => {
    try {
      await adminApi.logout();
    } catch (error) {
      // Continue with logout even if the API call fails; the server clears the
      // cookies and we drop local state regardless.
      console.warn('Logout API call failed:', error);
    } finally {
      setAdmin(null);
    }
  };

  const value = {
    admin,
    isLoading,
    login,
    logout,
    isAuthenticated: !!admin,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};